// src/slices/userManagement/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, UserFilters, UserPagination, UserModalState } from 'pages/UserManagement/types';
import {
  fetchUsers,
  fetchUserById,
  createNewUser,
  updateExistingUser,
  deleteUserById,
  toggleStatus,
  uploadAvatar,
} from './thunk';

interface UserManagementState {
  users: User[];
  selectedUser: User | null;
  pagination: UserPagination;
  filters: UserFilters;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  loading: boolean;
  error: string | null;
  modalState: UserModalState;
}

const initialState: UserManagementState = {
  users: [],
  selectedUser: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: {
    searchTerm: '',
    departmentId: undefined,
    roleId: undefined,
    status: 'All',
  },
  sortBy: 'name',
  sortOrder: 'asc',
  loading: false,
  error: null,
  modalState: {
    create: false,
    edit: false,
    delete: false,
    avatar: false,
    permissions: false,
  },
};

const userManagementSlice = createSlice({
  name: 'UserManagement',
  initialState,
  reducers: {
    setUsers: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
      state.loading = false;
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    addUser: (state, action: PayloadAction<User>) => {
      state.users.push(action.payload);
    },
    updateUser: (state, action: PayloadAction<User>) => {
      const index = state.users.findIndex(u => u.id === action.payload.id);
      if (index !== -1) {
        state.users[index] = action.payload;
      }
    },
    deleteUser: (state, action: PayloadAction<number>) => {
      state.users = state.users.filter(u => u.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setPagination: (state, action: PayloadAction<Partial<UserPagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setFilters: (state, action: PayloadAction<Partial<UserFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        searchTerm: '',
        departmentId: undefined,
        roleId: undefined,
        status: 'All',
      };
      state.pagination.currentPage = 1;
    },
    setSorting: (state, action: PayloadAction<{ field: string; order: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.field;
      state.sortOrder = action.payload.order;
    },
    openModal: (state, action: PayloadAction<keyof UserModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof UserModalState>) => {
      state.modalState[action.payload] = false;
    },
    closeAllModals: (state) => {
      state.modalState = {
        create: false,
        edit: false,
        delete: false,
        avatar: false,
        permissions: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.users = payload.items || payload.data || payload || [];
        state.pagination.totalItems = payload.totalItems ?? payload.total ?? state.users.length;
        state.pagination.totalPages = payload.totalPages ?? Math.ceil(state.pagination.totalItems / state.pagination.pageSize);
        state.pagination.currentPage = payload.currentPage ?? state.pagination.currentPage;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
            // Fetch user by ID
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.selectedUser = action.payload as unknown as User;
      })
      
      // Create user
      .addCase(createNewUser.pending, (state) => {
        state.loading = true;
      })
            .addCase(createNewUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.unshift(action.payload as unknown as User);
        state.pagination.totalItems += 1;
        state.modalState.create = false;
      })
      .addCase(createNewUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update user
      .addCase(updateExistingUser.pending, (state) => {
        state.loading = true;
      })
            .addCase(updateExistingUser.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as unknown as User;
        const index = state.users.findIndex(u => u.id === payload.id);
        if (index !== -1) {
          state.users[index] = payload;
        }
        state.modalState.edit = false;
        state.selectedUser = null;
      })
      .addCase(updateExistingUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete user
      .addCase(deleteUserById.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(u => u.id !== action.payload);
        state.pagination.totalItems -= 1;
        state.modalState.delete = false;
        state.selectedUser = null;
      })
      .addCase(deleteUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
            // Toggle status
      .addCase(toggleStatus.fulfilled, (state, action) => {
        const user = action.payload as unknown as User;
        const index = state.users.findIndex(u => u.id === user.id);
        if (index !== -1) {
          state.users[index] = user;
        }
      })
      
      // Upload avatar
      .addCase(uploadAvatar.fulfilled, (state, action) => {
        const { id, avatarUrl } = action.payload;
        const index = state.users.findIndex(u => u.id === id);
        if (index !== -1) {
          state.users[index].avatar = avatarUrl;
        }
        if (state.selectedUser?.id === id) {
          state.selectedUser.avatar = avatarUrl;
        }
        state.modalState.avatar = false;
      });
  },
});

export const {
  setUsers,
  setSelectedUser,
  addUser,
  updateUser,
  deleteUser,
  setLoading,
  setError,
  clearError,
  setPage,
  setPageSize,
  setPagination,
  setFilters,
  clearFilters,
  setSorting,
  openModal,
  closeModal,
  closeAllModals,
} = userManagementSlice.actions;

export default userManagementSlice.reducer;